/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    LastViewedPosition,
    SetSoCuLastViewedPositionRequest,
    SetVodLastViewedPositionRequest,
    SoCuLastViewedPositionApi,
    VodLastViewedPositionApi,
} from 'client-code/last-viewed-position';
import { IVodSocuLastViewedPositionServiceRepository } from '../interfaces/IVodSocuLastViewedPositionServiceRepository';
import { Log } from '../logging/Log';
import { TServicesParams, TVodSocuLastViewedPositionParams } from '../types';
import { Constants } from '../constants';
import { CommonHeaders } from '../CommonHeaders';

export class VodSocuLastViewedPositionRepositoryImpl
    implements IVodSocuLastViewedPositionServiceRepository
{
    mParams: TServicesParams;

    TAG: string = 'VodSocuLastViewedPositionRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getRequestParameters(commonHeaders) {
        return {
            applicationName: commonHeaders.ApplicationName,
            deviceType: commonHeaders.DeviceType,
            productName: commonHeaders.ProductName,
            acceptLanguage: commonHeaders['Accept-Language'],
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            contentType: commonHeaders['Content-Type'],
            whiny: this.mParams.whiny || 'false',
        };
    }

    private getConfigurationParameters(commonHeaders: CommonHeaders) {
        const configurationParameters: ConfigurationParameters = {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders.headers,
            },
        };
        return configurationParameters;
    }

    private async setSocuLastViewedPositionAtService(
        socuLVPParams: TVodSocuLastViewedPositionParams,
    ): Promise<void> {
        try {
            const { screenType } = socuLVPParams;
            const commonHeaders = new CommonHeaders(screenType);
            const configurationParameters: ConfigurationParameters =
                this.getConfigurationParameters(commonHeaders);

            const lastViewedPositionRequest: LastViewedPosition = {
                lastViewedPosition: socuLVPParams.lastViewedPosition,
                partnerOfferId: socuLVPParams.partnerOfferId,
                providerPartnerId: socuLVPParams.providerPartnerId,
                contentId: socuLVPParams.contentId,
                collectionId: socuLVPParams.collectionId,
                duration: socuLVPParams.duration,
            };
            const requestParameters: SetSoCuLastViewedPositionRequest = {
                ...this.getRequestParameters(commonHeaders.headers),
                lastViewedPosition: lastViewedPositionRequest,
            };
            const configuration = new Configuration(configurationParameters);
            const lvpApi = new SoCuLastViewedPositionApi(configuration);
            await lvpApi.setSoCuLastViewedPosition(requestParameters);
        } catch (error) {
            Log.e(this.TAG, 'setSocuLastViewedPositionAtService', error);
            throw error;
        }
    }

    private async setVodLastViewedPositionAtService(
        vodLVPParams: TVodSocuLastViewedPositionParams,
    ): Promise<void> {
        try {
            const { screenType } = vodLVPParams;
            const commonHeaders = new CommonHeaders(screenType);
            const configurationParameters: ConfigurationParameters =
                this.getConfigurationParameters(commonHeaders);
            const lastViewedPositionRequestParams: LastViewedPosition = {
                lastViewedPosition: vodLVPParams.lastViewedPosition,
                partnerOfferId: vodLVPParams.partnerOfferId,
                providerPartnerId: vodLVPParams.providerPartnerId,
                contentId: vodLVPParams.contentId,
                collectionId: vodLVPParams.collectionId,
                duration: vodLVPParams.duration,
            };

            const requestParameters: SetVodLastViewedPositionRequest = {
                ...this.getRequestParameters(commonHeaders.headers),
                lastViewedPosition: lastViewedPositionRequestParams,
            };
            const configuration = new Configuration(configurationParameters);
            const lvpApi = new VodLastViewedPositionApi(configuration);
            await lvpApi.setVodLastViewedPosition(requestParameters);
        } catch (error) {
            Log.e(this.TAG, 'setVodLastViewedPositionAtService', error);
            throw error;
        }
    }

    public async setSocuLastViewedPosition(
        socuLVPParams: TVodSocuLastViewedPositionParams,
    ): Promise<void> {
        try {
            return await this.setSocuLastViewedPositionAtService(socuLVPParams);
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }

    public async setVodLastViewedPosition(
        vodLVPParams: TVodSocuLastViewedPositionParams,
    ): Promise<void> {
        try {
            return await this.setVodLastViewedPositionAtService(vodLVPParams);
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }
}
